<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { GOAL_STRUCTURE_EVENT_NAMES } from './goal_structure_event_names'
import { betaOptIn } from '@/util/beta_opt_in'
import { AccessPointsUtil } from '@/util/access_points'
import type { AccessPointLevel } from '@/util/access_points'
import { useSendOnArrival } from './useSendOnArrival'
import type { Navigate } from './useSendOnArrival'

type AccessPointTab = {
  level: AccessPointLevel
  uuid: string
  label: string
}

const props = withDefaults(defineProps<{
  activeAccessPoint?: AccessPointLevel
  accessPointUuids: Record<AccessPointLevel, string>
}>(), {
  activeAccessPoint: AccessPointsUtil.valuesMap.GRADE_LEVEL
})

defineSlots<{
  default(): unknown
}>()

const route = useRoute()
const analytics = getAnalyticsTracker()
const { followLinkThenSend } = useSendOnArrival()

const tabs = computed((): AccessPointTab[] =>
  AccessPointsUtil.orderedValues.map((level) => ({
    level,
    uuid: props.accessPointUuids[level],
    label: `${AccessPointsUtil.labelsMap[level]} Goal`
  }))
)

const keptPageQuery = computed(() => ({ draft: route.query.draft, ...betaOptIn(route.query) }))

// A link only follows itself on Enter, but a tab is also expected to activate on Space
function activateWithSpace(event: KeyboardEvent) {
  (event.currentTarget as HTMLElement).click()
}

function clickedTab(tab: AccessPointTab, navigate: Navigate, event: MouseEvent) {
  return followLinkThenSend(navigate, event, () => analytics.sendEvent(GOAL_STRUCTURE_EVENT_NAMES.CLICKED_ACCESS_POINT_TAB, {
    access_point_level_clicked: tab.level,
    access_point_uuid_clicked: tab.uuid
  }))
}
</script>

<template>
  <!-- #1c5498 is the anchor page accent, which has no design-system token yet -->
  <TabsRoot :model-value="activeAccessPoint" activation-mode="manual">
    <TabsList aria-label="Access Point Levels" class="flex border-b-8 border-[#1c5498] pt-1">
      <router-link
        v-for="tab in tabs"
        :key="tab.level"
        v-slot="{ href, navigate, isExactActive }"
        :to="{ name: 'AnchorPage', params: { uuid: tab.uuid }, query: keptPageQuery }"
        custom
      >
        <TabsTrigger :value="tab.level" as-child>
          <a
            :href="href"
            :aria-current="isExactActive ? 'page' : undefined"
            class="block w-1/3 rounded-t-xs border border-transparent py-3 text-center text-text-2xl font-medium leading-snug text-[#1c5498] hover:border-b-gray-300 hover:border-x-gray-200 hover:border-t-gray-200 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 data-[state=active]:border-[#1c5498] data-[state=active]:bg-[#1c5498] data-[state=active]:text-white max-sm:px-0.5 max-sm:py-2.5 max-sm:text-md"
            :data-testid="`access-point-tab-${tab.level}`"
            @click="clickedTab(tab, navigate, $event)"
            @keydown.space.prevent="activateWithSpace"
          >{{ tab.label }}</a>
        </TabsTrigger>
      </router-link>
    </TabsList>
    <TabsContent
      v-for="tab in tabs"
      :key="tab.level"
      :value="tab.level"
      class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
    >
      <slot />
    </TabsContent>
  </TabsRoot>
</template>
